import { useState, useEffect } from 'react';
import authService from '../services/authService';
import { doc, getDoc, setDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../services/firebase';  // CORRECTED PATH
import firebaseStorage from '../services/firebaseStorage';  // CORRECTED PATH
import telemetry from '../services/telemetry'; // Import telemetry service
import clubService from '../services/clubService';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [clubInfo, setClubInfo] = useState(null);
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    // Listen to auth state changes
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        console.log('🔵 useAuth: Firebase user detected:', firebaseUser.email);
        console.log('🔵 useAuth: emailVerified:', firebaseUser.emailVerified);

        // Always fetch fresh user data from Firestore
        const userData = await getUserData(firebaseUser.uid);
        console.log('🔵 useAuth: Firestore userData:', userData);

        const userObj = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified, // Include email verification status
          displayName: firebaseUser.displayName || userData.name,
          name: userData.name,
          phoneNumber: userData.phoneNumber,
          clubInfo: userData.clubInfo,
          clubId: userData.clubInfo?.clubId, // Also include clubId at top level for easier access
          role: userData.role,
          additionalRoles: userData.additionalRoles || [], // Explicitly include additionalRoles
          profilePicture: userData.profilePicture || null,
          createdAt: userData.createdAt || null,
          registrationCompleted: userData.registrationCompleted || false,
          registrationCompletedAt: userData.registrationCompletedAt || null,
          joinedClubs: userData.joinedClubs || {},
          joinedClubsIds: userData.joinedClubsIds || [],
          permissions: userData.permissions || null
        };

        console.log('🔵 useAuth: Setting user object:', userObj);

        // TELEMETRY: Identify the user
        if (telemetry) {
          telemetry.setUser(firebaseUser.uid);
          telemetry.setUserProperties({
            role: userData.role,
            clubId: userData.clubInfo?.clubId || 'none'
          });
        }

        setUser(userObj);
        setUserRole(userData.role);
        setClubInfo(userData.clubInfo);
        setPermissions(userData.permissions);
      } else {
        console.log('🔵 useAuth: No Firebase user');
        setUser(null);
        setUserRole(null);
        setClubInfo(null);
        setPermissions(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Get complete user data from Firestore - ALWAYS fetch fresh
  const getUserData = async (uid) => {
    try {
      console.log('🔵 getUserData: Fetching from Firestore for uid:', uid);

      // Always get directly from Firestore
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('✅ getUserData: Found user document:', userData);
        return {
          role: userData.role || 'player',
          additionalRoles: userData.additionalRoles || [], // Explicitly include additionalRoles
          clubInfo: userData.clubInfo || null,
          permissions: userData.permissions || null,
          name: userData.name || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          profilePicture: userData.profilePicture || null,
          createdAt: userData.createdAt || null,
          registrationCompleted: userData.registrationCompleted || false,
          registrationCompletedAt: userData.registrationCompletedAt || null,
          joinedClubs: userData.joinedClubs || {},
          joinedClubsIds: userData.joinedClubsIds || []
        };
      }

      console.log('⚠️ getUserData: No user document found, returning defaults');
      return {
        role: 'player',
        clubInfo: null,
        permissions: null,
        name: '',
        email: '',
        phoneNumber: '',
        profilePicture: null,
        createdAt: null,
        registrationCompleted: false,
        registrationCompletedAt: null,
        joinedClubs: {},
        joinedClubsIds: []
      };
    } catch (error) {
      console.error('❌ getUserData: Error getting user data:', error);
      return {
        role: 'player',
        clubInfo: null,
        permissions: null,
        name: '',
        email: '',
        profilePicture: null,
        createdAt: null,
        registrationCompleted: false,
        registrationCompletedAt: null,
        joinedClubs: {},
        joinedClubsIds: []
      };
    }
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return userRole === 'super-admin';
  };

  // Check if user is club admin for specific club
  const isClubAdmin = (clubId) => {
    if (isSuperAdmin()) return true;
    return permissions?.clubAdminOf?.includes(clubId) || false;
  };

  // Check if user has access to club
  const hasClubAccess = (clubId) => {
    if (isSuperAdmin()) return true;
    if (isClubAdmin(clubId)) return true;
    return clubInfo?.clubId === clubId;
  };

  // Get all clubs user can administer
  const getAdministeredClubs = async () => {
    try {
      if (isSuperAdmin()) {
        return await getAllClubs();
      } else if (permissions?.clubAdminOf?.length > 0) {
        const clubs = [];
        for (const clubId of permissions.clubAdminOf) {
          const clubData = await firebaseStorage.get(`club:${clubId}`);
          if (clubData) {
            clubs.push(JSON.parse(clubData.value));
          }
        }
        return clubs;
      }
      return [];
    } catch (error) {
      console.error('Error getting administered clubs:', error);
      return [];
    }
  };

  // Get all clubs
  const getAllClubs = async () => {
    try {
      // Use firebaseStorage to get all clubs from Firestore
      const clubs = await firebaseStorage.getAllFromCollection('club');
      return clubs;
    } catch (error) {
      console.error('Error getting all clubs:', error);
      return [];
    }
  };

  // Find club by code
  const findClubByCode = async (clubCode) => {
    try {
      // Use firebaseStorage query to find club by code
      const clubs = await firebaseStorage.query('club', 'code', '==', clubCode);
      return clubs.length > 0 ? clubs[0] : null;
    } catch (error) {
      console.error('Error finding club:', error);
      return null;
    }
  };

  // Add player to team with club info
  const addPlayerToTeam = async (club, playerEmail, playerName, clubInfo) => {
    try {
      if (!club.teams || club.teams.length === 0) {
        console.error('No teams available in club');
        return false;
      }

      const teamId = club.teams[0];
      const teamData = await firebaseStorage.get(`team:${teamId}`);

      if (!teamData) {
        console.error('Team not found');
        return false;
      }

      const team = JSON.parse(teamData.value);

      const existingPlayer = team.players?.find(p => p.email === playerEmail);
      if (existingPlayer) {
        console.log('Player already in team');
        return true;
      }

      const playerId = `player-${Date.now()}`;

      const newPlayer = {
        id: playerId,
        name: playerName,
        email: playerEmail,
        teamId: team.id,
        clubInfo: clubInfo,
        joinedAt: new Date().toISOString()
      };

      if (!team.players) team.players = [];
      team.players.push(newPlayer);

      await firebaseStorage.set(`team:${teamId}`, JSON.stringify(team));

      console.log('Player added to team successfully with club info');
      return true;
    } catch (error) {
      console.error('Error adding player to team:', error);
      return false;
    }
  };

  // Sign in function
  const login = async (email, password) => {
    try {
      const result = await authService.signIn(email, password);
      if (result.success && result.user) {
        const userData = await getUserData(result.user.uid);
        setUserRole(userData.role);
        setClubInfo(userData.clubInfo);
        setPermissions(userData.permissions);

        // TELEMETRY: Log login success
        if (telemetry) {
          telemetry.logEvent('login_success', {
            method: 'email'
          });
        }
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Unable to sign in. Please try again.' };
    }
  };

  // Sign up function - FIXED to save to Firestore
  const signup = async (email, name, password, role, clubCode = null, phoneNumber = null) => {
    try {
      let clubInfo = null;
      let permissions = null;

      // Handle different roles
      if (role === 'player' && clubCode) {
        const club = await findClubByCode(clubCode);
        if (!club) {
          return { success: false, error: 'Invalid club code. Please check and try again.' };
        }

        clubInfo = {
          clubId: club.id,
          clubName: club.name,
          clubCode: club.code,
          clubLocation: club.location,
          role: 'player', // Added to explicitly define the user's role
          joinedAt: new Date().toISOString(),
          status: 'pending' // Requires admin approval
        };
      } else if (role === 'admin') {
        permissions = {
          clubAdminOf: []
        };
      }

      // Create Firebase Authentication account
      const result = await authService.signUp(email, password);

      if (result.success && result.user) {
        const uid = result.user.uid;

        const userData = {
          uid: uid,
          email: email,
          name: name,
          phoneNumber: phoneNumber || '',
          role: role,
          clubInfo: role === 'player' ? clubInfo : null,
          permissions: permissions,
          createdAt: new Date().toISOString()
        };

        console.log('Creating user document in Firestore:', userData);

        // Save to Firestore users collection
        try {
          const userDocRef = doc(db, 'users', uid);
          await setDoc(userDocRef, userData);
          console.log('✅ User document created in Firestore successfully');
        } catch (firestoreError) {
          console.error('❌ Error saving to Firestore:', firestoreError);
          throw firestoreError;
        }

        // Add player to team if applicable
        if (role === 'player' && clubCode && clubInfo) {
          const club = await findClubByCode(clubCode);
          if (club) {
            await addPlayerToTeam(club, email, name, clubInfo);
          }
        }

        setUserRole(role);
        setClubInfo(clubInfo);
        setPermissions(permissions);
        return { success: true, user: result.user };
      }
      return { success: false, error: result.error };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Unable to create account. Please try again.' };
    }
  };

  // Onboard a new club (public self-serve registration)
  const onboardClub = async (email, adminName, password, clubName, location) => {
    try {
      // 1. Create Firebase Authentication account
      const result = await authService.signUp(email, password);

      if (result.success && result.user) {
        const uid = result.user.uid;

        // 2. Create the Club in pending state
        const clubResult = await clubService.createClub(uid, clubName, location, 'pending', email, adminName);
        
        if (!clubResult.success) {
          // If club creation fails, we should ideally rollback user creation, but for now we throw error
          throw new Error(clubResult.error || 'Failed to create club document.');
        }

        const clubId = clubResult.id;

        const permissions = {
          clubAdminOf: [clubId]
        };

        const clubInfo = {
          clubId: clubId,
          role: 'admin',
          status: 'pending',
          joinedAt: new Date().toISOString()
        };

        const joinedClubs = {
          [clubId]: {
            clubId: clubId,
            role: 'admin',
            status: 'pending',
            joinedAt: new Date().toISOString()
          }
        };

        const userData = {
          uid: uid,
          email: email,
          name: adminName,
          phoneNumber: '',
          role: 'admin',
          clubInfo: clubInfo,
          joinedClubs: joinedClubs,
          joinedClubsIds: [clubId],
          permissions: permissions,
          createdAt: new Date().toISOString()
        };

        console.log('Creating admin user document in Firestore:', userData);

        // 3. Save to Firestore users collection
        const userDocRef = doc(db, 'users', uid);
        await setDoc(userDocRef, userData);

        // 4. Trigger email to Super Admin using Firebase extension (mail collection)
        try {
          const mailRef = collection(db, 'mail');
          await addDoc(mailRef, {
            to: ['kashyap.uppuluri@gmail.com'], // Or query for super-admins
            message: {
              subject: `New Club Registration: ${clubName}`,
              html: `
                <h2>New Club Registration Pending Approval</h2>
                <p><strong>Club Name:</strong> ${clubName}</p>
                <p><strong>Location:</strong> ${location}</p>
                <p><strong>Admin Name:</strong> ${adminName}</p>
                <p><strong>Admin Email:</strong> ${email}</p>
                <br/>
                <p>Please log in to the <a href="https://clubhudl.com/login">ClubHudl Super Admin Dashboard</a> to approve or reject this request.</p>
              `
            }
          });
        } catch (mailError) {
          console.error('Error queueing notification email:', mailError);
          // Don't fail the registration if email fails
        }

        setUserRole('admin');
        setClubInfo(clubInfo);
        setPermissions(permissions);
        
        return { success: true, user: result.user };
      }
      return { success: false, error: result.error };
    } catch (error) {
      console.error('Onboard club error:', error);
      return { success: false, error: error.message || 'Unable to register club. Please try again.' };
    }
  };

  // Add user as club admin
  const addClubAdmin = async (userEmail, clubId) => {
    try {
      if (!isSuperAdmin()) {
        return { success: false, error: 'Only super admins can add club admins' };
      }

      const userData = await firebaseStorage.get(`user:${userEmail}`);
      if (!userData) {
        return { success: false, error: 'User not found' };
      }

      const user = JSON.parse(userData.value);

      if (!user.permissions) {
        user.permissions = {
          clubAdminOf: []
        };
      }

      if (!user.permissions.clubAdminOf.includes(clubId)) {
        user.permissions.clubAdminOf.push(clubId);
      }

      await firebaseStorage.set(`user:${userEmail}`, JSON.stringify(user));

      const clubData = await firebaseStorage.get(`club:${clubId}`);
      if (clubData) {
        const club = JSON.parse(clubData.value);
        if (!club.admins) club.admins = [];
        if (!club.admins.includes(userEmail)) {
          club.admins.push(userEmail);
        }
        await firebaseStorage.set(`club:${clubId}`, JSON.stringify(club));
      }

      return { success: true };
    } catch (error) {
      console.error('Error adding club admin:', error);
      return { success: false, error: 'Unable to add club admin. Please try again.' };
    }
  };

  // Remove club admin
  const removeClubAdmin = async (userEmail, clubId) => {
    try {
      if (!isSuperAdmin()) {
        return { success: false, error: 'Only super admins can remove club admins' };
      }

      const userData = await firebaseStorage.get(`user:${userEmail}`);
      if (!userData) {
        return { success: false, error: 'User not found' };
      }

      const user = JSON.parse(userData.value);

      if (user.permissions?.clubAdminOf) {
        user.permissions.clubAdminOf = user.permissions.clubAdminOf.filter(id => id !== clubId);
      }

      await firebaseStorage.set(`user:${userEmail}`, JSON.stringify(user));

      const clubData = await firebaseStorage.get(`club:${clubId}`);
      if (clubData) {
        const club = JSON.parse(clubData.value);
        if (club.admins) {
          club.admins = club.admins.filter(email => email !== userEmail);
        }
        await firebaseStorage.set(`club:${clubId}`, JSON.stringify(club));
      }

      return { success: true };
    } catch (error) {
      console.error('Error removing club admin:', error);
      return { success: false, error: 'Unable to remove club admin. Please try again.' };
    }
  };

  // Promote admin to super-admin
  const promoteToSuperAdmin = async (userEmail) => {
    try {
      if (!isSuperAdmin()) {
        return { success: false, error: 'Only super admins can promote users to super admin' };
      }

      const userData = await firebaseStorage.get(`user:${userEmail}`);
      if (!userData) {
        return { success: false, error: 'User not found' };
      }

      const user = JSON.parse(userData.value);

      if (user.role === 'super-admin') {
        return { success: false, error: 'User is already a super admin' };
      }

      if (user.role !== 'admin') {
        return { success: false, error: 'Only admins can be promoted to super admin' };
      }

      user.role = 'super-admin';

      if (!user.permissions) {
        user.permissions = {};
      }
      user.permissions.canCreateClubs = true;
      user.permissions.canManageAllClubs = true;

      await firebaseStorage.set(`user:${userEmail}`, JSON.stringify(user));

      return { success: true };
    } catch (error) {
      console.error('Error promoting to super admin:', error);
      return { success: false, error: 'Unable to promote user. Please try again.' };
    }
  };

  // Demote super-admin to regular admin
  const demoteFromSuperAdmin = async (userEmail) => {
    try {
      if (!isSuperAdmin()) {
        return { success: false, error: 'Only super admins can demote super admins' };
      }

      if (user && user.email === userEmail) {
        return { success: false, error: 'You cannot demote yourself' };
      }

      const userData = await firebaseStorage.get(`user:${userEmail}`);
      if (!userData) {
        return { success: false, error: 'User not found' };
      }

      const targetUser = JSON.parse(userData.value);

      if (targetUser.role !== 'super-admin') {
        return { success: false, error: 'User is not a super admin' };
      }

      targetUser.role = 'admin';

      if (targetUser.permissions) {
        delete targetUser.permissions.canCreateClubs;
        delete targetUser.permissions.canManageAllClubs;
      }

      await firebaseStorage.set(`user:${userEmail}`, JSON.stringify(targetUser));

      return { success: true };
    } catch (error) {
      console.error('Error demoting super admin:', error);
      return { success: false, error: 'Unable to demote user. Please try again.' };
    }
  };

  // Sign out function
  const logout = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setUserRole(null);
      setClubInfo(null);
      setPermissions(null);
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      return { success: false, error: 'Unable to sign out. Please try again.' };
    }
  };

  return {
    user,
    currentUser: user,
    userRole,
    clubInfo,
    permissions,
    loading,

    // Auth functions
    login,
    signup,
    onboardClub,
    logout,
    signIn: login,
    signUp: signup,
    signOut: logout,

    // Permission checks
    isSuperAdmin,
    isClubAdmin,
    hasClubAccess,

    // Admin management
    addClubAdmin,
    removeClubAdmin,
    promoteToSuperAdmin,
    demoteFromSuperAdmin,
    getAdministeredClubs,
    getAllClubs
  };
};

export default useAuth;